import React, { useState } from 'react';
import { SearchIcon, ChevronRightIcon, BriefcaseIcon, FileTextIcon, ClipboardCheckIcon, ShieldIcon } from 'lucide-react';
import { CategorySection } from '../components/CategorySection';
interface Service {
  id: string;
  title: string;
  description: string;
  category: string;
  estimatedTime: string;
  fee: string;
}
const services: Service[] = [
// New Business Licenses
{
  id: 'bl1',
  title: 'General Business License',
  description: 'Basic business license for retail, office, or service-based operations',
  category: 'New Business Licenses',
  estimatedTime: '10-15 business days',
  fee: '$150.00'
}, {
  id: 'bl2',
  title: 'Restaurant & Food Service License',
  description: 'License for food preparation and service establishments',
  category: 'New Business Licenses',
  estimatedTime: '15-20 business days',
  fee: '$250.00'
}, {
  id: 'bl3',
  title: 'Professional Services License',
  description: 'License for consulting, medical, legal, or financial services',
  category: 'New Business Licenses',
  estimatedTime: '7-10 business days',
  fee: '$200.00'
}, {
  id: 'bl4',
  title: 'Contractor License',
  description: 'License for construction, renovation, or trade contractors',
  category: 'New Business Licenses',
  estimatedTime: '10-15 business days',
  fee: '$300.00'
},
// License Renewals & Modifications
{
  id: 'rm1',
  title: 'Annual License Renewal',
  description: 'Renew your existing business license',
  category: 'License Renewals & Modifications',
  estimatedTime: '5-7 business days',
  fee: '$100.00'
}, {
  id: 'rm2',
  title: 'Business Information Update',
  description: 'Update business name, address, or ownership information',
  category: 'License Renewals & Modifications',
  estimatedTime: '3-5 business days',
  fee: '$50.00'
}, {
  id: 'rm3',
  title: 'License Classification Change',
  description: 'Modify your business license type or classification',
  category: 'License Renewals & Modifications',
  estimatedTime: '7-10 business days',
  fee: '$75.00'
}, {
  id: 'rm4',
  title: 'Temporary License Extension',
  description: 'Request temporary extension of license validity',
  category: 'License Renewals & Modifications',
  estimatedTime: '2-3 business days',
  fee: '$25.00'
},
// Operational Permits
{
  id: 'op1',
  title: 'Special Event Permit',
  description: 'Permit for hosting temporary events or promotions',
  category: 'Operational Permits',
  estimatedTime: '5-7 business days',
  fee: '$100.00'
}, {
  id: 'op2',
  title: 'Outdoor Seating Permit',
  description: 'Permit for outdoor dining or service areas',
  category: 'Operational Permits',
  estimatedTime: '7-10 business days',
  fee: '$150.00'
}, {
  id: 'op3',
  title: 'Extended Hours Permit',
  description: 'Permission for extended business operating hours',
  category: 'Operational Permits',
  estimatedTime: '5-7 business days',
  fee: '$75.00'
}, {
  id: 'op4',
  title: 'Mobile Business Permit',
  description: 'Permit for food trucks or mobile services',
  category: 'Operational Permits',
  estimatedTime: '7-10 business days',
  fee: '$200.00'
},
// Inspections & Compliance
{
  id: 'ic1',
  title: 'Health & Safety Inspection',
  description: 'Schedule mandatory health and safety inspection',
  category: 'Inspections & Compliance',
  estimatedTime: '3-5 business days',
  fee: '$125.00'
}, {
  id: 'ic2',
  title: 'Fire Safety Compliance',
  description: 'Fire safety inspection and certification',
  category: 'Inspections & Compliance',
  estimatedTime: '3-5 business days',
  fee: '$150.00'
}, {
  id: 'ic3',
  title: 'Code Compliance Review',
  description: 'Business premises code compliance verification',
  category: 'Inspections & Compliance',
  estimatedTime: '5-7 business days',
  fee: '$100.00'
}, {
  id: 'ic4',
  title: 'Annual Compliance Certificate',
  description: 'Yearly compliance certification for regulated businesses',
  category: 'Inspections & Compliance',
  estimatedTime: '7-10 business days',
  fee: '$175.00'
}];
const CategoryIcon = ({
  category
}: {
  category: string;
}) => {
  switch (category) {
    case 'New Business Licenses':
      return <BriefcaseIcon className="h-6 w-6 text-blue-600" data-id="element-3519" />;
    case 'License Renewals & Modifications':
      return <FileTextIcon className="h-6 w-6 text-green-600" data-id="element-3520" />;
    case 'Operational Permits':
      return <ClipboardCheckIcon className="h-6 w-6 text-purple-600" data-id="element-3521" />;
    case 'Inspections & Compliance':
      return <ShieldIcon className="h-6 w-6 text-orange-600" data-id="element-3522" />;
    default:
      return <BriefcaseIcon className="h-6 w-6 text-gray-600" data-id="element-3523" />;
  }
};
export const BusinessLicensingPage = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const filteredServices = services.filter(service => service.title.toLowerCase().includes(searchQuery.toLowerCase()) || service.description.toLowerCase().includes(searchQuery.toLowerCase()));
  const categories = Array.from(new Set(filteredServices.map(service => service.category)));
  return <div className="min-h-screen bg-gray-50" data-id="element-3524">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-id="element-3525">
        {/* Breadcrumb */}
        <div className="flex items-center text-sm text-gray-500 mb-8" data-id="element-3526">
          <a href="/" className="hover:text-gray-700" data-id="element-3527">
            Dashboard
          </a>
          <ChevronRightIcon className="h-4 w-4 mx-2" data-id="element-3528" />
          <span className="text-gray-900" data-id="element-3529">
            Business & Operational Licensing
          </span>
        </div>
        {/* Header */}
        <div className="mb-8" data-id="element-3530">
          <h1 className="text-3xl font-bold text-gray-900 mb-2" data-id="element-3531">
            Business & Operational Licensing
          </h1>
          <p className="text-gray-600" data-id="element-3532">
            Apply for, renew, or modify business licenses and operational
            permits
          </p>
        </div>
        {/* Search */}
        <div className="mb-8" data-id="element-3533">
          <div className="max-w-md relative" data-id="element-3534">
            <div className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" data-id="element-3535">
              <SearchIcon className="h-5 w-5" data-id="element-3536" />
            </div>
            <input type="text" className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md text-sm" placeholder="Search services..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} data-id="element-3537" />
          </div>
        </div>
        {/* Service Categories */}
        {categories.map(category => <CategorySection key={category} category={category} services={filteredServices.filter(service => service.category === category)} icon={<CategoryIcon category={category} data-id="element-3539" />} buttonText="Apply Now" data-id="element-3538" />)}
      </div>
    </div>;
};